import { FunctionComponent, h } from 'preact'
import { MutableRef, useEffect } from 'preact/hooks'
import { useContext } from 'preact/compat'
import cls from 'classnames'

import { FieldEntry, NextFields, Page, SurveyFieldProps } from 'scripts/type'
import { useBrandingProps, usePostSurveyHeightChangeMessage } from 'scripts/hooks'
import { Branding, FormContainer, SafeArea } from 'components/ui'
import BaseContext from 'contexts/base-context'
import SurveyContext from 'contexts/survey-context'

import FieldsPage from './FieldsPage'
import useRefs from './useRefs'
import classes from './survey.module.scss'

export type SurveyFormProps = SurveyFieldProps & {
  onFieldChange: (data: Page) => void
  onFieldHeightChange?: (height: number) => void
  onPageComplete?: (entryId: string, fieldsEntry: FieldEntry[]) => void
  onClose?: () => void
  editingCode?: string
}

const SurveyForm: FunctionComponent<SurveyFormProps> = (props) => {
  const { activePageIndex } = useContext(SurveyContext)
  const { isPreview, interactive } = useContext(BaseContext)
  const { pages, onFieldChange, onFieldHeightChange, onClose } = props
  const { brandingRef, prevActivePageIndexRef, safeAreaRef } = useRefs()
  const { postHeightChange } = usePostSurveyHeightChangeMessage(brandingRef as MutableRef<HTMLDivElement>)

  const { brandingUrl, brandingVisible } = useBrandingProps()

  useEffect(() => {
    prevActivePageIndexRef.current = activePageIndex
  }, [activePageIndex])

  const handleFieldHeightChange = (fieldHeight: number) => {
    const widgetHeight = postHeightChange(fieldHeight, safeAreaRef?.current?.getHeight() ?? 0)
    isPreview && onFieldHeightChange?.(widgetHeight)
  }

  const handleChange = (data?: NextFields) => {
    onFieldChange?.(data!)
  }

  const getFieldPageClassNames = (pageIndex: number) => {
    if (!interactive) {
      return []
    }
    if (activePageIndex === pageIndex) {
      // 当前激活页面为第一页的情况下，是不需要动画的，需要立即显示出来，避免 Widget 弹出后出现空白。
      if (pageIndex === 0) {
        return [classes.fieldPageShow, classes.fieldPageShowImmediately]
      }
      return [classes.fieldPageShow]
    } else if (pageIndex < activePageIndex) {
      // 上一次激活的页面，应该走正常出场动画
      if (prevActivePageIndexRef.current === pageIndex) {
        return [classes.fieldPageAfterShow]
      }
      // 通过字段规则跳转跳过的页面，走立即隐藏动画
      return [classes.fieldPageAfterShowImmediately]
    }
    return [classes.fieldPageReadyShow]
  }

  return (
    <FormContainer onClose={onClose} closeStyle={{ top: '4px', insetInlineEnd: '4px' }} type="survey">
      <div className={classes.content}>
        {pages.map((page, index) => {
          const fieldPageClassNames = getFieldPageClassNames(index)
          return (
            <FieldsPage
              key={index}
              page={page}
              className={cls(fieldPageClassNames)}
              isActivePage={index === activePageIndex || (isPreview && !interactive)}
              onFieldChange={handleChange}
              onFieldHeightChange={handleFieldHeightChange}
              onPageComplete={props.onPageComplete}
            />
          )
        })}
      </div>
      {brandingVisible && <Branding brandingUrl={brandingUrl!} ref={brandingRef} />}
      <SafeArea ref={safeAreaRef} />
    </FormContainer>
  )
}

export default SurveyForm
